import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY    = 'lpg_company_auth';
const SA_STORAGE_KEY = 'lpg_sa_auth';

export function AuthProvider({ children }) {
  const [auth, setAuth]         = useState(null);  // company user auth
  const [saAuth, setSaAuth]     = useState(null);  // super admin auth
  const [loading, setLoading]   = useState(true);

  // Restore from localStorage on mount, then validate
  useEffect(() => {
    const stored   = localStorage.getItem(STORAGE_KEY);
    const saStored = localStorage.getItem(SA_STORAGE_KEY);

    const init = async () => {
      if (saStored) {
        try {
          const parsed = JSON.parse(saStored);
          const res = await axios.get(`${API}/superadmin/me`, {
            headers: { Authorization: `Bearer ${parsed.token}` },
          });
          const updated = { ...parsed, admin: res.data };
          setSaAuth(updated);
          localStorage.setItem(SA_STORAGE_KEY, JSON.stringify(updated));
        } catch {
          localStorage.removeItem(SA_STORAGE_KEY);
        }
      }
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${parsed.token}` },
          });
          const updated = { ...parsed, user: res.data.user, company: res.data.company };
          setAuth(updated);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      setLoading(false);
    };

    init();
  }, []);

  // ── Company login ────────────────────────────────────────────────────────
  const login = async (email, password, companyCode) => {
    const res = await axios.post(`${API}/auth/login`, {
      email, password, company_code: companyCode,
    });
    const data = { token: res.data.token, user: res.data.user, company: res.data.company };
    setAuth(data);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return data;
  };

  const logout = () => {
    setAuth(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  // ── Super admin login ────────────────────────────────────────────────────
  const saLogin = async (email, password) => {
    const res = await axios.post(`${API}/superadmin/login`, { email, password });
    const data = { token: res.data.token, admin: res.data.admin };
    setSaAuth(data);
    localStorage.setItem(SA_STORAGE_KEY, JSON.stringify(data));
    return data;
  };

  const saLogout = () => {
    setSaAuth(null);
    localStorage.removeItem(SA_STORAGE_KEY);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const refreshAuth = async () => {
    if (!auth?.token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      const updated = { ...auth, user: res.data.user, company: res.data.company };
      setAuth(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch {
      logout();
    }
  };

  const authHeader = () =>
    auth?.token ? { Authorization: `Bearer ${auth.token}` } : {};

  const saAuthHeader = () =>
    saAuth?.token ? { Authorization: `Bearer ${saAuth.token}` } : {};

  return (
    <AuthContext.Provider value={{
      // company auth
      auth, login, logout, refreshAuth, authHeader,
      isLoggedIn:    !!auth,
      user:          auth?.user,
      company:       auth?.company,
      role:          auth?.user?.role,
      companyActive: auth?.company?.is_active !== false,

      // super admin auth
      saAuth, saLogin, saLogout, saAuthHeader,
      isSuperAdmin:  !!saAuth,

      loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
